import React, { useState, useEffect, useRef } from 'react';
import { Clock } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';

const CourseCard = ({ course, index, darkMode, onSelectCourse }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div 
      ref={ref} 
      className="widget-card"
      onClick={() => { if (navigator.vibrate) navigator.vibrate(10); course.available && onSelectCourse && onSelectCourse(course.name); }}
      style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        padding: '25px',
        cursor: course.available ? 'pointer' : 'default',
        border: (course.active || course.started) ? '2px solid #1cb0f6' : (darkMode ? '2px solid #444' : '2px solid #e5e5e5'),
        backgroundColor: (course.active || course.started) ? '#1cb0f6' : (darkMode ? 'rgba(43, 43, 43, 0.2)' : 'rgba(255, 255, 255, 0.2)'),
        backdropFilter: (course.active || course.started) ? 'none' : 'blur(10px)',
        WebkitBackdropFilter: (course.active || course.started) ? 'none' : 'blur(10px)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        position: 'relative',
        borderRadius: '16px',
        boxShadow: (course.active || course.started) ? '0 4px 0 #148ac2' : '0 4px 0 rgba(0,0,0,0.05)',
        opacity: isVisible ? 1 : 0,
        animation: isVisible ? `pop-up 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) ${index * 0.1}s both` : 'none'
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-5px)';
        if (!course.active && !course.started) e.currentTarget.style.boxShadow = '0 8px 15px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        if (!course.active && !course.started) e.currentTarget.style.boxShadow = '0 4px 0 rgba(0,0,0,0.05)';
      }}
    >
      {!course.available && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          backgroundColor: darkMode ? '#444' : '#e5e5e5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Clock size={16} color="#888" />
        </div>
      )}
      <img src={course.icon} alt={course.name} style={{ width: '60px', height: '60px', marginBottom: '15px', filter: course.available ? 'none' : 'grayscale(100%)', opacity: course.available ? 1 : 0.5 }} />
      <h3 style={{ margin: '0 0 5px 0', color: (course.active || course.started) ? '#fff' : (darkMode ? '#fff' : '#4b4b4b'), fontSize: '1.1rem' }}>{course.name}</h3>
      <p style={{ margin: 0, fontSize: '0.85rem', color: (course.active || course.started) ? '#ccecfc' : '#afafaf', fontWeight: '600', visibility: course.available ? 'visible' : 'hidden' }}>{course.learners}</p>
    </div>
  );
};

function Courses({ darkMode, onSelectCourse, completedLessons = [], activeCourse }) {
  const [learnerCounts, setLearnerCounts] = useState({});

  useEffect(() => {
    const fetchLearnerCounts = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "users"));
        const counts = {};
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const lessons = data.completedLessons || [];
          
          const allCourses = ['HTML', 'Python', 'JavaScript', 'Kotlin', 'Java', 'C++', 'C#', 'SQL', 'React', 'TypeScript', 'Swift', 'Go', 'Rust'];
          
          allCourses.forEach(course => {
            if (lessons.includes(`${course}_1`)) {
              counts[course] = (counts[course] || 0) + 1;
            }
          });
        });
        setLearnerCounts(counts);
      } catch (error) {
        console.error("Error fetching learner counts:", error);
      }
    };

    fetchLearnerCounts();
  }, []);

  const getLearners = (courseName) => {
    const count = learnerCounts[courseName] || 0;
    return `${count} Lernende`;
  };

  const courses = [
    { name: 'HTML', learners: getLearners('HTML'), icon: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/html5/html5-original.svg', active: activeCourse === 'HTML', started: completedLessons.includes('HTML_1'), available: true },
    { name: 'Python', learners: getLearners('Python'), icon: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/python/python-original.svg', active: activeCourse === 'Python', started: completedLessons.includes('Python_1'), available: true },
    { name: 'JavaScript', learners: getLearners('JavaScript'), icon: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/javascript/javascript-original.svg', active: activeCourse === 'JavaScript', started: completedLessons.includes('JavaScript_1'), available: true },
    { name: 'Kotlin', learners: getLearners('Kotlin'), icon: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/kotlin/kotlin-original.svg', active: activeCourse === 'Kotlin', started: completedLessons.includes('Kotlin_1'), available: true },
    { name: 'Java', learners: getLearners('Java'), icon: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/java/java-original.svg', started: completedLessons.includes('Java_1'), available: false },
    { name: 'C++', learners: getLearners('C++'), icon: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/cplusplus/cplusplus-original.svg', started: completedLessons.includes('C++_1'), available: false },
    { name: 'C#', learners: getLearners('C#'), icon: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/csharp/csharp-original.svg', started: completedLessons.includes('C#_1'), available: false },
    { name: 'SQL', learners: getLearners('SQL'), icon: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/mysql/mysql-original.svg', started: completedLessons.includes('SQL_1'), available: false },
    { name: 'React', learners: getLearners('React'), icon: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/react/react-original.svg', started: completedLessons.includes('React_1'), available: false },
    { name: 'TypeScript', learners: getLearners('TypeScript'), icon: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/typescript/typescript-original.svg', started: completedLessons.includes('TypeScript_1'), available: false },
    { name: 'Swift', learners: getLearners('Swift'), icon: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/swift/swift-original.svg', started: completedLessons.includes('Swift_1'), available: false },
    { name: 'Go', learners: getLearners('Go'), icon: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/go/go-original.svg', started: completedLessons.includes('Go_1'), available: false },
    { name: 'Rust', learners: getLearners('Rust'), icon: 'https://raw.githubusercontent.com/devicons/devicon/master/icons/rust/rust-original.svg', started: completedLessons.includes('Rust_1'), available: false },
  ];

  return (
    <main className="main-content" style={{ height: '100vh', overflowY: 'auto', paddingBottom: '40px', paddingLeft: '10px', paddingRight: '10px' }}>
      <style>{`
        @keyframes pop-up {
          0% { transform: scale(0.95); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      <div className="main-content-header" style={{ position: 'sticky', top: '20px', zIndex: 10, backgroundColor: '#1cb0f6', borderBottom: '4px solid #148ac2', borderRadius: '16px', color: 'white', marginBottom: '20px', marginTop: '20px' }}>
        <h2>Kurse</h2>
        <span>Wähle einen neuen Kurs</span>
      </div>

      <div style={{ width: '100%', maxWidth: '800px', margin: '0 auto', boxSizing: 'border-box', padding: '0 10px' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '15px' 
        }}>
          {courses.map((course, index) => (
            <CourseCard 
              key={index} 
              course={course} 
              index={index} 
              darkMode={darkMode} 
              onSelectCourse={onSelectCourse} 
            />
          ))}
        </div>
      </div>
    </main>
  );
}

export default Courses;
